<template>
  <div v-if="tableData" class="space-y-48 lg:space-y-64">
    <div
      v-for="({ title, sections, separator }, tableGroupIndex) in tableData"
      :key="`table-group-${tableGroupIndex}`"
      :class="{
        'border-static-default-low border-t pt-48 lg:pt-64': separator,
      }"
    >
      <p class="punchline mb-12">{{ title }}</p>
      <div class="divide-static-default-low space-y-48 divide-y lg:space-y-64">
        <div
          v-for="(
            { title: sectionTitle, trackingKey, lines, textBlock }, sectionIndex
          ) in sections"
          :id="trackingKey"
          :key="`${tableGroupIndex}_${sectionIndex}`"
          ref="sectionRef"
          class="pt-48 first:pt-0 lg:pt-64"
        >
          <p v-if="sectionTitle" class="heading-2 mb-8">
            {{ sectionTitle }}
          </p>
          <div class="space-y-20">
            <div
              v-for="(line, lineIndex) in lines"
              :key="`${tableGroupIndex}_${sectionIndex}_${lineIndex}_head`"
            >
              <div v-if="line.type === 'scores'">
                <div :class="gridClasses">
                  <div
                    v-for="(score, scoreIndex) in line.values"
                    :key="`${tableGroupIndex}_${sectionIndex}_${lineIndex}_head_${scoreIndex}`"
                  >
                    <ProductScore
                      v-if="score"
                      :label="sectionTitle || ''"
                      :value="score"
                    />
                  </div>
                </div>
              </div>
              <div
                v-else-if="line.type === 'ratings' || line.type === 'colors'"
              >
                <p v-if="line.title" class="body-1-bold mb-8">
                  {{ line.title }}
                </p>
                <div
                  class="bg-surface-default-mid rounded-lg"
                  :class="gridClasses"
                >
                  <div
                    v-for="(value, valueIndex) in line.values"
                    :key="`${tableGroupIndex}_${sectionIndex}_${lineIndex}_head_${valueIndex}`"
                  >
                    <div v-if="line.type === 'ratings'">
                      <RevRating
                        v-if="typeof value === 'number'"
                        class="p-12"
                        :score="value || 0"
                        size="small"
                      />
                    </div>
                    <div v-else-if="line.type === 'colors'">
                      <ColorSwatches
                        v-if="typeof value === 'object'"
                        class="!justify-start p-12"
                        :colors="value as Colors"
                        size="medium"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <!-- nosemgrep: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -->
            <div
              v-if="textBlock?.format === 'html'"
              :class="$style.textBlock"
              v-html="textBlock.value"
            />
            <div
              v-for="(line, lineIndex) in lines"
              :key="`${tableGroupIndex}_${sectionIndex}_${lineIndex}_body`"
            >
              <div
                v-if="line.type === 'info'"
                class="bg-static-info-mid rounded-sm hover:bg-static-info-mid-hover"
              >
                <RevAccordionItem class="!p-16 hover:bg-transparent">
                  <template #title>
                    <div class="flex items-center">
                      <IconLightbulb class="mr-8" />
                      <span class="body-2-bold">{{
                        i18n(translations.importanceTitle)
                      }}</span>
                    </div>
                  </template>
                  <template #body>
                    <p class="-mt-8 pl-24">{{ line.value }}</p>
                  </template>
                </RevAccordionItem>
              </div>
              <div
                v-else-if="!['scores', 'colors', 'ratings'].includes(line.type)"
              >
                <p v-if="line.title" class="body-1-bold mb-8">
                  {{ line.title }}
                </p>
                <div
                  class="bg-surface-default-mid rounded-lg"
                  :class="gridClasses"
                >
                  <div
                    v-for="(value, valueIndex) in line.values"
                    :key="`${tableGroupIndex}_${sectionIndex}_${lineIndex}_body_${valueIndex}`"
                  >
                    <ul
                      v-if="line.type === 'tags'"
                      class="flex list-none flex-wrap items-start gap-x-4 gap-y-8 p-12"
                    >
                      <li
                        v-for="(label, labelIndex) in value"
                        :key="`${tableGroupIndex}_${sectionIndex}_${lineIndex}_${labelIndex}`"
                        class="flex"
                      >
                        <RevTag
                          v-if="typeof label === 'string'"
                          :label
                          variant="secondary"
                          variation="outline"
                        />
                      </li>
                    </ul>
                    <p v-else class="text-static-info-hi heading-2 p-12">
                      {{ value }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevRating } from '@ds/components/Rating'
import { RevTag } from '@ds/components/Tag'
import { IconLightbulb } from '@ds/icons/IconLightbulb'
import { useIntersectionObserver, useTemplateRefsList } from '@vueuse/core'

import type { TableData } from '../../../models/productComparison.ts'
import ColorSwatches from '../../ColorSwatches.vue'
import ProductScore from '../../ProductScore.vue'
import type { Colors } from '../ProductComparison.types'

import translations from './ProductComparisonTableContent.translations'

const { nbColumns = 4, withTracking = false } = defineProps<{
  tableData: TableData[]
  nbColumns?: 2 | 4
  withTracking?: boolean
}>()

const gridClasses = computed(() => [
  'grid grid-cols-2 gap-x-24  lg:gap-x-40',
  {
    'lg:grid-cols-4': nbColumns === 4,
  },
])

const i18n = useI18n()

const { trackComparisonBlockImpression } = useTracking()
const blockImpressionTracked = ref<Array<string>>([])

function trackImpression(blockId: string) {
  if (blockImpressionTracked.value.includes(blockId)) return
  blockImpressionTracked.value.push(blockId)
  trackComparisonBlockImpression({
    block_name: blockId,
  })
}

const sectionRef = useTemplateRefsList<HTMLElement>()

function initBlockImpressionTracking() {
  sectionRef.value.forEach((element) => {
    if (!element.id) return
    const rect = element.getBoundingClientRect()
    const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight
    if (isVisible) trackImpression(element.id)
  })
}

if (withTracking) {
  const { stop } = useIntersectionObserver(
    sectionRef,
    ([{ isIntersecting, target }]) => {
      if (isIntersecting && target.id) trackImpression(target.id)
    },
    { threshold: 0.1 },
  )

  onMounted(() => {
    initBlockImpressionTracking()
  })

  onBeforeUnmount(() => {
    stop()
  })
}
</script>

<style module>
.textBlock {
  p,
  li {
    margin-top: 16px;
  }
}
</style>
